import { FC } from 'react';

import { Alert, Badge, Modal, theme, Typography } from '@/shared/design-system';
import type { PermissionDraftChange, PermissionDraftSummary } from '@/utils/permission-draft';

import { formatMemberCount } from '../../role-descriptions';
import { ChangeList, ChangeRow, ChangeSign, ChangeText, ReviewBody } from './styled';

export type ReviewChangesModalProps = {
  open: boolean;
  roleName: string;
  summary: PermissionDraftSummary;
  memberCount: number;
  /** The caller's own requests settle on submit, so the change lands immediately. */
  appliesImmediately: boolean;
  isSubmitting: boolean;
  onClose: () => void;
  onConfirm: () => void;
};

const plural = (count: number, noun: string) => `${count} ${noun}${count === 1 ? '' : 's'}`;

export const reviewImpactCopy = (memberCount: number, appliesImmediately: boolean) => {
  if (!memberCount) return 'Nobody holds this role yet, so no one gains or loses access today.';

  return appliesImmediately
    ? `${formatMemberCount(memberCount)} will see this change as soon as you confirm.`
    : `${formatMemberCount(memberCount)} will see this change once it is approved.`;
};

export const reviewOutcomeCopy = (changeCount: number, appliesImmediately: boolean) =>
  appliesImmediately
    ? `You can approve role changes, so ${plural(changeCount, 'change')} will apply right away. Each one is still recorded in the role's activity.`
    : `You can't approve role changes, so this sends ${plural(changeCount, 'request')} to approvers. They show as Pending on this role until someone decides.`;

export const confirmChangesLabel = (changeCount: number, appliesImmediately: boolean) =>
  appliesImmediately
    ? `Apply ${plural(changeCount, 'change')}`
    : `Send ${plural(changeCount, 'request')}`;

const ChangeItem: FC<{ change: PermissionDraftChange; added: boolean }> = ({ change, added }) => (
  <ChangeRow>
    <ChangeSign $added={added} aria-label={added ? 'Added' : 'Removed'}>
      {added ? '+' : '−'}
    </ChangeSign>
    <ChangeText>
      <Typography variant="body3" color={theme.colors.text.primary}>
        {change.permission.label}
      </Typography>
      <Typography variant="body4" color={theme.colors.text.tertiary}>
        {change.groupLabel}
      </Typography>
    </ChangeText>
    {change.permission.sensitive ? (
      <Badge size="sm" variant="subtle" color="yellow" label="Sensitive" />
    ) : (
      <span />
    )}
  </ChangeRow>
);

/**
 * The last stop before a permission draft becomes requests: what changes, who it reaches and
 * whether it lands now or waits for an approver. Role changes are audited and some cannot be
 * undone without another approval, so this step is never skipped.
 */
export const ReviewChangesModal: FC<ReviewChangesModalProps> = ({
  open,
  roleName,
  summary,
  memberCount,
  appliesImmediately,
  isSubmitting,
  onClose,
  onConfirm,
}) => {
  const changeCount = summary.added.length + summary.removed.length;

  return (
    <Modal
      open={open}
      onOpenChange={(isOpen) => !isOpen && onClose()}
      variant="center"
      maxWidth={560}
      title={`Review changes to ${roleName}`}
      description={reviewImpactCopy(memberCount, appliesImmediately)}
      primaryAction={{
        label: isSubmitting ? 'Submitting…' : confirmChangesLabel(changeCount, appliesImmediately),
        onClick: onConfirm,
        disabled: isSubmitting || !changeCount,
      }}
      secondaryAction={{ label: 'Back to editing', onClick: onClose, disabled: isSubmitting }}
    >
      <ReviewBody>
        <ChangeList>
          {summary.added.map((change) => (
            <ChangeItem key={change.permission.permission} change={change} added />
          ))}
          {summary.removed.map((change) => (
            <ChangeItem key={change.permission.permission} change={change} added={false} />
          ))}
        </ChangeList>

        <Alert
          variant={appliesImmediately ? 'default' : 'info'}
          description={reviewOutcomeCopy(changeCount, appliesImmediately)}
        />
      </ReviewBody>
    </Modal>
  );
};
